import { FilterModel } from 'types/filterModel'
import { FC, useCallback, useEffect, useRef, useState, useMemo } from 'react'
import useTranslation from 'next-translate/useTranslation'
import { SlugPath } from 'types/slugPath'
import FilterMainSlidePageRedesign, {
  FilterMainSliderNavigationActionType
} from './filterMainSlidePageRedesign'
import FilterMobileTabletButtonsRedesign, {
  FilterMobileTabletButtonsActionType
} from './filterMobileTabletButtonsRedesign'
import ContentCategoryMenu from '@/components/organisms/contentCategoryMenu'
import FilterColorsSlidePage from '@/components/organisms/filter/filterMobile/filterColorsSlidePage'
import FilterManufacturersSlidePage from '@/components/organisms/filter/filterMobile/filterManufacturersSlidePage'
import FilterSizesSlidePage from '@/components/organisms/filter/filterMobile/filterSizesSlidePage'
import {
  useActiveFilterDispatch,
  useFilter
} from '@/providers/filterProvider/filtersProvider'
import { ContentCategoryMenuLayoutType } from '@/components/organisms/contentCategoryMenu/contentCategoryMenu'
import FilterGendersSlidePage from './filterGendersSlidePage'
import FilterCategoriesSlidePage from './filterCategoriesSlidePage'
import { Category } from '@/network/graphql.g'
import FilterTagsSlidePage from './filterTagsSlidePage'
import { Box } from '@/components/atoms/Grid'
import styled from 'styled-components'
import FilterMaterialsSlidePage from './filterMaterialsSlidePage'
import FilterMotivesSlidePage from './filterMotivesSlidePage'
import SlideMenuRedesign, {
  SlidePage,
  SlideMenuActions
} from '@/components/organisms/slideMenuRedesign'
import FilterSlidePageFooterRedesign from '@/components/organisms/filter/filterMobile/filterSlidePageFooterRedesign'
import { FilterQueryItem } from '@/types/filterQueryItem'
import StickyFilterButton from '@/components/molecules/stickyFilterButton'

enum ScrollDirection {
  Up = 'up',
  Down = 'down',
  InitialUp = 'initialUp',
  InitialDown = 'InitialDown'
}

type PendingFilterState = Partial<Record<string, string[]>>

type ActiveSubPage = {
  type: FilterMainSliderNavigationActionType
  subType?: string
  title: string
} | null

const useScrollDirection = (): ScrollDirection => {
  const [scrollDirection, setScrollDirection] = useState<ScrollDirection>(
    ScrollDirection.InitialDown
  )

  useEffect(() => {
    let lastScrollY = window.pageYOffset
    const updateScrollDirection = () => {
      const scrollY = window.pageYOffset
      const direction =
        scrollY < 260
          ? scrollY > lastScrollY || scrollY < 167
            ? ScrollDirection.InitialDown
            : scrollDirection === ScrollDirection.InitialDown
            ? scrollDirection
            : ScrollDirection.InitialUp
          : scrollY > lastScrollY
          ? ScrollDirection.Down
          : ScrollDirection.Up

      if (
        direction !== scrollDirection &&
        (scrollY - lastScrollY > 1 || scrollY - lastScrollY < -1)
      ) {
        setScrollDirection(direction)
      }

      lastScrollY = scrollY > 0 ? scrollY : 0
    }
    window.addEventListener('scroll', updateScrollDirection)
    return () => {
      window.removeEventListener('scroll', updateScrollDirection)
    }
  }, [scrollDirection])

  return scrollDirection
}

export type TabletMobileFilterProps = {
  isTablet?: boolean
  filter: FilterModel
  slugPath: SlugPath
  subcategories: Category[]
  showInStorage?: boolean
  showInSale?: boolean
  showInStorageCheckbox?: boolean
  isSticky?: boolean
  isParfumCampaign?: boolean
} & Rest

const TabletMobileFilterRedesign: FC<TabletMobileFilterProps> = ({
  filter,
  slugPath,
  subcategories,
  isTablet,
  showInStorage = true,
  showInSale = true,
  showInStorageCheckbox = true,
  isSticky = false,
  isParfumCampaign,
  ...rest
}) => {
  const { sortTypes } = filter
  const { t } = useTranslation('common')
  const { activeFilters } = useFilter()
  const { discardAllFilter, setFiltersForType } = useActiveFilterDispatch()
  const slideMenuRef = useRef<SlideMenuActions>(null)

  const [pendingFilters, setPendingFilters] = useState<PendingFilterState>({})
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [activeSlideIndex, setActiveSlideIndex] = useState(0)
  const [activeSubPage, setActiveSubPage] = useState<ActiveSubPage>(null)
  const [openCategoryMenu, setOpenCategoryMenu] = useState(false)

  const scrollDirection = useScrollDirection()

  const updatePendingFilter = useCallback(
    (type: string, selectedIds: string[]) => {
      setPendingFilters((prev) => ({
        ...prev,
        [type]: selectedIds
      }))
    },
    []
  )

  const handleResetPending = (type: string) => {
    if (activeSubPage?.type === FilterMainSliderNavigationActionType.tag) {
      updatePendingFilter(`TAG_${activeSubPage.subType}`, [])
      return
    }

    updatePendingFilter(type, [])
  }

  const getInitialIds = useCallback(
    (type: FilterQueryItem): string[] => {
      if (pendingFilters[type] !== undefined) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return pendingFilters[type]!
      }
      switch (type) {
        case FilterQueryItem.GENDER:
          return activeFilters.genders?.map((i) => i.id) || []
        case FilterQueryItem.CATEGORY:
          return activeFilters.categories?.map((i) => i.id) || []
        case FilterQueryItem.COLOR:
          return activeFilters.colors?.map((i) => i.id) || []
        case FilterQueryItem.SIZE:
          return activeFilters.sizes?.map((i) => i.id) || []
        case FilterQueryItem.MATERIAL:
          return activeFilters.materials?.map((i) => i.id) || []
        case FilterQueryItem.PATTERN:
          return activeFilters.patterns?.map((i) => i.id) || []
        case FilterQueryItem.MANUFACTURER:
          return activeFilters.manufacturers?.map((i) => i.id) || []
        default:
          return []
      }
    },
    [activeFilters, pendingFilters]
  )

  const getInitialTagIds = useCallback(
    (tagSectionId: string): string[] => {
      const key = `TAG_${tagSectionId}`
      if (pendingFilters[key] !== undefined) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return pendingFilters[key]!
      }
      const activeTagSection = activeFilters.tags?.find(
        (t) => t.id === tagSectionId
      )
      return activeTagSection ? activeTagSection.items.map((i) => i.id) : []
    },
    [activeFilters, pendingFilters]
  )

  const handleNavigationClick = useCallback(
    (
      navigationActionType: FilterMainSliderNavigationActionType,
      navigationSubActionType?: string
    ) => {
      let title = ''

      switch (navigationActionType) {
        case FilterMainSliderNavigationActionType.gender:
          title = t('Category.Filter.gender')
          break
        case FilterMainSliderNavigationActionType.category:
          title = t('Category.Filter.category')
          break
        case FilterMainSliderNavigationActionType.color:
          title = t('Category.Filter.color')
          break
        case FilterMainSliderNavigationActionType.pattern:
          title = t('Category.Filter.motive')
          break
        case FilterMainSliderNavigationActionType.material:
          title = t('Category.Filter.material')
          break
        case FilterMainSliderNavigationActionType.manufacturer:
          title = t('Category.Filter.manufacturer')
          break
        case FilterMainSliderNavigationActionType.size:
          title = t('Category.Filter.size')
          break
        case FilterMainSliderNavigationActionType.tag:
          // eslint-disable-next-line no-case-declarations
          const tag = filter.tags.find(
            ({ id }) => id === navigationSubActionType
          )
          title = tag?.title || ''
          break
      }

      setActiveSubPage({
        type: navigationActionType,
        subType: navigationSubActionType,
        title
      })

      slideMenuRef?.current?.slideNext()
    },
    [t, filter.tags]
  )

  const onCancelFilter = () => {
    discardAllFilter()
    setPendingFilters({})
  }

  const onFilter = (shouldClose: boolean, specificType?: string) => {
    const applyType = (key: string, ids: string[]) => {
      if (key.startsWith('TAG_')) {
        const tagSectionId = key.replace('TAG_', '')
        setFiltersForType(FilterQueryItem.TAG, ids, tagSectionId)
      } else {
        setFiltersForType(key as FilterQueryItem, ids)
      }
    }

    if (specificType) {
      const pendingIds = pendingFilters[specificType]
      if (pendingIds !== undefined) {
        applyType(specificType, pendingIds)
      }
    } else {
      Object.keys(pendingFilters).forEach((key) => {
        const ids = pendingFilters[key]
        if (ids !== undefined) {
          applyType(key, ids)
        }
      })
    }

    if (shouldClose) {
      slideMenuRef.current?.close()
    }
  }

  const handleFooterButtonAction = () => {
    if (activeSlideIndex === 0) {
      slideMenuRef.current?.close()
    } else {
      const currentType =
        getActiveSubPageFilterType() ||
        (activeSubPage?.type === FilterMainSliderNavigationActionType.tag
          ? `TAG_${activeSubPage.subType}`
          : undefined)

      if (currentType) {
        onFilter(false, currentType)
      }
      slideMenuRef.current?.slidePrev()
    }
  }

  const handleButtonAction = (
    actionType: FilterMobileTabletButtonsActionType
  ) => {
    switch (actionType) {
      case FilterMobileTabletButtonsActionType.filter:
        setIsMenuOpen(true)
        setOpenCategoryMenu(false)
        setActiveSubPage(null)
        break
      case FilterMobileTabletButtonsActionType.category:
        setIsMenuOpen(true)
        setOpenCategoryMenu(true)
        break
    }
  }

  const renderSubPageContent = () => {
    if (!activeSubPage) {
      return <></>
    }
    const { type, subType } = activeSubPage

    switch (type) {
      case FilterMainSliderNavigationActionType.gender:
        return (
          <FilterGendersSlidePage
            genders={filter.genders}
            initialSelections={getInitialIds(FilterQueryItem.GENDER)}
            updatePendingFilter={(ids) =>
              updatePendingFilter(FilterQueryItem.GENDER, ids)
            }
          />
        )
      case FilterMainSliderNavigationActionType.category:
        return (
          <FilterCategoriesSlidePage
            categories={filter.categories}
            initialSelections={getInitialIds(FilterQueryItem.CATEGORY)}
            updatePendingFilter={(ids) =>
              updatePendingFilter(FilterQueryItem.CATEGORY, ids)
            }
          />
        )
      case FilterMainSliderNavigationActionType.color:
        return (
          <FilterColorsSlidePage
            colors={filter.colors}
            initialSelections={getInitialIds(FilterQueryItem.COLOR)}
            updatePendingFilter={(ids) =>
              updatePendingFilter(FilterQueryItem.COLOR, ids)
            }
          />
        )
      case FilterMainSliderNavigationActionType.pattern:
        return (
          <FilterMotivesSlidePage
            motives={filter.patterns}
            initialSelections={getInitialIds(FilterQueryItem.PATTERN)}
            updatePendingFilter={(ids) =>
              updatePendingFilter(FilterQueryItem.PATTERN, ids)
            }
          />
        )
      case FilterMainSliderNavigationActionType.material:
        return (
          <FilterMaterialsSlidePage
            materials={filter.materials}
            initialSelections={getInitialIds(FilterQueryItem.MATERIAL)}
            updatePendingFilter={(ids) =>
              updatePendingFilter(FilterQueryItem.MATERIAL, ids)
            }
          />
        )
      case FilterMainSliderNavigationActionType.manufacturer:
        return (
          <FilterManufacturersSlidePage
            manufacturers={filter.manufacturers}
            initialSelections={getInitialIds(FilterQueryItem.MANUFACTURER)}
            updatePendingFilter={(ids) =>
              updatePendingFilter(FilterQueryItem.MANUFACTURER, ids)
            }
          />
        )
      case FilterMainSliderNavigationActionType.size:
        return (
          <FilterSizesSlidePage
            sizes={filter.sizes}
            initialSelections={getInitialIds(FilterQueryItem.SIZE)}
            updatePendingFilter={(ids) =>
              updatePendingFilter(FilterQueryItem.SIZE, ids)
            }
          />
        )
      case FilterMainSliderNavigationActionType.tag:
        // eslint-disable-next-line no-case-declarations
        const tag = filter.tags.find(({ id }) => id === subType)
        return (
          <FilterTagsSlidePage
            tags={tag?.items ?? []}
            initialSelections={getInitialTagIds(tag?.id || '')}
            updatePendingFilter={(ids) =>
              updatePendingFilter(`TAG_${tag?.id}`, ids)
            }
          />
        )
      default:
        return <></>
    }
  }

  const getActiveSubPageFilterType = () => {
    if (!activeSubPage) {
      return undefined
    }
    switch (activeSubPage.type) {
      case FilterMainSliderNavigationActionType.gender:
        return FilterQueryItem.GENDER
      case FilterMainSliderNavigationActionType.category:
        return FilterQueryItem.CATEGORY
      case FilterMainSliderNavigationActionType.color:
        return FilterQueryItem.COLOR
      case FilterMainSliderNavigationActionType.pattern:
        return FilterQueryItem.PATTERN
      case FilterMainSliderNavigationActionType.material:
        return FilterQueryItem.MATERIAL
      case FilterMainSliderNavigationActionType.manufacturer:
        return FilterQueryItem.MANUFACTURER
      case FilterMainSliderNavigationActionType.size:
        return FilterQueryItem.SIZE
      default:
        return undefined
    }
  }

  const isResetDisabledCalculation = () => {
    if (activeSlideIndex > 0) {
      const type = getActiveSubPageFilterType()
      if (
        !type &&
        activeSubPage?.type !== FilterMainSliderNavigationActionType.tag
      ) {
        return true
      }

      const currentKey = type || `TAG_${activeSubPage?.subType}`

      if (pendingFilters[currentKey] !== undefined) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return pendingFilters[currentKey]!.length === 0
      }
      if (activeSubPage?.type === FilterMainSliderNavigationActionType.tag) {
        return getInitialTagIds(activeSubPage?.subType || '').length === 0
      }
      return getInitialIds(type as FilterQueryItem).length === 0
    }

    const hasAnyActive = Object.values(activeFilters).some(
      (v) => Array.isArray(v) && v.length > 0
    )
    return !hasAnyActive
  }

  const hasUnconfirmedChanges = () => {
    const type = getActiveSubPageFilterType()

    const currentKey =
      type ||
      (activeSubPage?.type === FilterMainSliderNavigationActionType.tag
        ? `TAG_${activeSubPage.subType}`
        : undefined)

    if (!currentKey) {
      return false
    }

    const pendingIds = pendingFilters[currentKey]

    if (pendingIds === undefined) {
      return false
    }

    let originalIds: string[] = []

    if (activeSubPage?.type === FilterMainSliderNavigationActionType.tag) {
      const tagId = activeSubPage.subType
      const activeTagSection = activeFilters.tags?.find((t) => t.id === tagId)
      originalIds = activeTagSection
        ? activeTagSection.items.map((i) => i.id)
        : []
    } else if (type) {
      switch (type) {
        case FilterQueryItem.GENDER:
          originalIds = activeFilters.genders?.map((i) => i.id) || []
          break
        case FilterQueryItem.CATEGORY:
          originalIds = activeFilters.categories?.map((i) => i.id) || []
          break
        case FilterQueryItem.COLOR:
          originalIds = activeFilters.colors?.map((i) => i.id) || []
          break
        case FilterQueryItem.SIZE:
          originalIds = activeFilters.sizes?.map((i) => i.id) || []
          break
        case FilterQueryItem.MATERIAL:
          originalIds = activeFilters.materials?.map((i) => i.id) || []
          break
        case FilterQueryItem.PATTERN:
          originalIds = activeFilters.patterns?.map((i) => i.id) || []
          break
        case FilterQueryItem.MANUFACTURER:
          originalIds = activeFilters.manufacturers?.map((i) => i.id) || []
          break
      }
    }

    if (pendingIds.length !== originalIds.length) {
      return true
    }

    const sortedPending = [...pendingIds].sort()
    const sortedOriginal = [...originalIds].sort()

    return JSON.stringify(sortedPending) !== JSON.stringify(sortedOriginal)
  }

  const slidePages = useMemo((): SlidePage[] => {
    if (!isMenuOpen) {
      return []
    }

    if (openCategoryMenu) {
      return [
        {
          menuWidth: isTablet ? 460 : '100vw',
          page: (
            <ContentCategoryMenu
              layoutType={ContentCategoryMenuLayoutType.INSIDE_MENU}
              slugPath={slugPath}
              subcategories={
                subcategories.find(({ id }) => id === slugPath.categoryId.id)
                  ?.children
              }
            />
          ),
          navigationBartTitle: t('Category.title')
        },
        { page: <></> }
      ]
    }

    const isResetDisabled = isResetDisabledCalculation()

    const mainPage: SlidePage = {
      menuWidth: isTablet ? 460 : '100vw',
      page: (
        <FilterMainSlidePageRedesign
          onActionClick={handleNavigationClick}
          priceRange={filter.priceRange}
          showGenders={filter.genders?.length > 0}
          showCategories={filter.categories?.length > 0}
          showSizes={filter.sizes?.length > 0}
          showColors={isParfumCampaign ? false : filter.colors?.length > 0}
          showPatterns={filter.patterns?.length > 0}
          showMaterials={filter.materials?.length > 0}
          showManufacturers={filter.manufacturers?.length > 0}
          showInStorage={showInStorage}
          showInSale={showInSale}
          showInStorageCheckbox={showInStorageCheckbox}
          tags={filter.tags}
          showSortTypes={filter?.sortTypes?.length > 0}
          sortTypes={filter?.sortTypes}
        />
      ),
      navigationBartTitle: t('Category.Filter.filterProducts'),
      isResetDisabled: isResetDisabled
    }

    const subPage: SlidePage = {
      menuWidth: isTablet ? 460 : '100vw',
      navigationBartTitle: activeSubPage?.title,
      page: renderSubPageContent(),
      filterType:
        activeSubPage?.type === FilterMainSliderNavigationActionType.tag
          ? FilterQueryItem.TAG
          : getActiveSubPageFilterType(),
      isResetDisabled: isResetDisabled
    }

    if (activeSubPage?.type === FilterMainSliderNavigationActionType.tag) {
      subPage.page = {
        ...subPage.page,
        props: { ...subPage.page.props, tagId: activeSubPage.subType }
      } as any
    }

    return [mainPage, subPage]
  }, [
    isMenuOpen,
    openCategoryMenu,
    activeSubPage,
    pendingFilters,
    filter,
    isTablet,
    slugPath,
    subcategories,
    handleNavigationClick,
    activeFilters,
    getInitialIds,
    getInitialTagIds,
    isResetDisabledCalculation
  ])

  const filterMobileTabletButtonsProps = {
    ...{
      activeFiltersCount: 30,
      sortTypes: sortTypes,
      showFilterButton: true,
      showCategoryButton: subcategories?.length > 0 && slugPath !== undefined,
      onDidTap: handleButtonAction,
      showInSale
    },
    ...rest
  }

  return (
    <>
      {isMenuOpen && (
        <SlideMenuRedesign
          defaultNavigationBartTitle={
            openCategoryMenu
              ? t('Category.title')
              : t('Category.Filter.filterProducts')
          }
          onDidClose={() => {
            setIsMenuOpen(false)
            setOpenCategoryMenu(false)
            setActiveSubPage(null)
            setActiveSlideIndex(0)
          }}
          onResetPendingFilter={handleResetPending}
          ref={slideMenuRef}
          pages={slidePages}
          onCancelFiltersDidTap={onCancelFilter}
          onSlideChange={(index) => setActiveSlideIndex(index)}
          stickyFooter={
            !openCategoryMenu ? (
              <FilterSlidePageFooterRedesign
                onFilterDidTap={handleFooterButtonAction}
                customTitle={
                  activeSlideIndex > 0
                    ? t('Category.Filter.confirmSelection')
                    : undefined
                }
                disabled={activeSlideIndex > 0 && !hasUnconfirmedChanges()}
              />
            ) : null
          }
        />
      )}
      {isSticky ? (
        <Box minHeight={90}>
          <StyledBox scrollDirection={scrollDirection}>
            <FilterMobileTabletButtonsRedesign
              {...filterMobileTabletButtonsProps}
            />
          </StyledBox>
        </Box>
      ) : (
        <FilterMobileTabletButtonsRedesign
          {...filterMobileTabletButtonsProps}
        />
      )}
      <StickyFilterButton
        hidden={isMenuOpen}
        onClick={() =>
          handleButtonAction(FilterMobileTabletButtonsActionType.filter)
        }
      />
    </>
  )
}

export default TabletMobileFilterRedesign

const StyledBox = styled(Box)`
  padding: ${({ scrollDirection }) =>
    [
      ScrollDirection.Up,
      ScrollDirection.Down,
      ScrollDirection.InitialUp
    ].includes(scrollDirection)
      ? '10px'
      : '0px'};
  top: ${({ scrollDirection }) =>
    [ScrollDirection.Up, ScrollDirection.InitialUp].includes(scrollDirection)
      ? '50px'
      : scrollDirection === ScrollDirection.Down
      ? '-61px'
      : 'unset'};
  left: 0;
  right: 0;
  z-index: 10;
  background-color: ${({ theme }) => theme.colors.background};
  position: ${({ scrollDirection }) =>
    [
      ScrollDirection.Up,
      ScrollDirection.Down,
      ScrollDirection.InitialUp
    ].includes(scrollDirection)
      ? 'fixed'
      : 'relative'};
  transition: ${({ scrollDirection }) =>
    [
      ScrollDirection.Up,
      ScrollDirection.Down,
      ScrollDirection.InitialUp
    ].includes(scrollDirection)
      ? 'top 0.2s ease-out'
      : 'none'};
`
